(ns logseq.outliner.tree
  "Provides tree fns and INode protocol"
  (:require [datascript.core :as d]
            [datascript.impl.entity :as de]
            [logseq.db :as ldb]))

(defprotocol INode
  (-save [this *txs-state conn opts])
  (-del [this *txs-state db]))

(defn ^:api blocks->vec-tree-data
  [blocks root {:keys [include-root? keep-block-tx-id?]}]
  (if-not (:db/id root)
    blocks
    (let [root-id (:db/id root)
          parent-blocks (group-by #(get-in % [:block/parent :db/id]) blocks)
          sort-fn (fn [parent]
                    (when-let [children (get parent-blocks parent)]
                      (ldb/sort-by-order children)))
          block-children (fn block-children [parent level]
                           (mapv (fn [m]
                                   (let [id (:db/id m)
                                         children (block-children id (inc level))]
                                     (cond-> (assoc m
                                                    :block/level level
                                                    :block/children children
                                                    :block/parent {:db/id parent})
                                       (not keep-block-tx-id?)
                                       (dissoc :block/tx-id))))
                                 (sort-fn parent)))
          children (block-children root-id 1)]
      (if include-root?
        [(cond-> (assoc root :block/children children)
           (not keep-block-tx-id?)
           (dissoc :block/tx-id))]
        children))))

(defn- get-root-and-page
  [db root-id]
  (cond
    (uuid? root-id)
    (let [e (d/entity db [:block/uuid root-id])]
      (if (ldb/page? e) [true e] [false e]))

    (number? root-id)
    (let [e (d/entity db root-id)]
      (if (ldb/page? e) [true e] [false e]))

    (string? root-id)
    (if-let [id (parse-uuid root-id)]
      [false (d/entity db [:block/uuid id])]
      [true (ldb/get-page db root-id)])

    :else
    [false root-id]))

;; TODO: entity can already be used as a tree
(defn blocks->vec-tree
  "`blocks` need to be in the same page."
  [db blocks root-id & {:as option}]
  (let [blocks (map (fn [b] (if (de/entity? b)
                              (assoc (into {} b) :db/id (:db/id b))
                              b)) blocks)
        [page? root] (get-root-and-page db root-id)]
    (if-not root ; custom query
      blocks
      (let [include-root? (or (not page?) (:link option))
            root (if include-root?
                   (some #(when (= (:db/id %) (:db/id root)) %) blocks)
                   root)]
        (blocks->vec-tree-data blocks root {:include-root? include-root?})))))

(defn- tree [parent->children root default-level]
  (let [root-id (:db/id root)
        nodes (fn nodes [parent-id level]
                (mapv (fn [b]
                        (let [b' (assoc b :block/level (inc level))
                              children (nodes (:db/id b) (inc level))]
                          (if (seq children)
                            (assoc b' :block/children children)
                            b')))
                      (let [parent {:db/id parent-id}]
                        (-> (get parent->children parent)
                            (ldb/sort-by-order)))))
        children (nodes root-id 1)
        root' (assoc root :block/level (or default-level 1))]
    (if (seq children)
      (assoc root' :block/children children)
      root')))

(defn ^:api block-entity->map
  [e]
  (if (de/entity? e)
    (cond-> {:db/id (:db/id e)
             :block/uuid (:block/uuid e)
             :block/parent {:db/id (:db/id (:block/parent e))}
             :block/page (:block/page e)}
      (:block/refs e)
      (assoc :block/refs (:block/refs e))
      (:block/children e)
      (assoc :block/children (:block/children e)))
    (cond-> e
      (:db/id (:block/parent e))
      (assoc :block/parent {:db/id (:db/id (:block/parent e))}))))

(defn ^:api filter-top-level-blocks
  [blocks]
  (let [id->blocks (zipmap (map :db/id blocks) blocks)]
    (filter #(nil?
              (id->blocks
               (:db/id (:block/parent (id->blocks (:db/id %)))))) blocks)))

(defn non-consecutive-blocks->vec-tree
  "`blocks` need to be in the same page."
  ([blocks]
   (non-consecutive-blocks->vec-tree blocks 1))
  ([blocks default-level]
   (let [blocks (map block-entity->map blocks)
         top-level-blocks (filter-top-level-blocks blocks)
         top-level-blocks' (ldb/sort-by-order top-level-blocks)
         parent->children (group-by :block/parent blocks)]
     (map #(tree parent->children % (or default-level 1)) top-level-blocks'))))

(defn get-sorted-block-and-children
  [db db-id & {:as opts}]
  (when db-id
    (when-let [root-block (d/entity db db-id)]
      (ldb/get-block-and-children db (:block/uuid root-block) opts))))
